import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Link as MUILink,
  useTheme,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { motion } from "framer-motion";
import SortlyBooksLogo from "../components/SortlyBooksLogo";
import { linkUserIfInvited } from "../utils/inviteHelpers"; // ✅ shared helper

export default function LoginForm() {
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get("email") || "";
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await linkUserIfInvited(cred.user); // ✅ attempt invite migration
      navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError("Invalid email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f9fbff",
        px: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 420,
            borderRadius: "16px",
            boxShadow: "0 6px 30px rgba(0,0,0,0.05)",
            backgroundColor: "#fff",
            textAlign: "center",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <SortlyBooksLogo />
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Your smart receipt & report assistant
            </Typography>
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ mb: 1.5, fontFamily: "Poppins, Inter" }}
          >
            Log in to your account
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 2 }}
          >
            Trusted by 1,000+ freelancers and small businesses
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoFocus
              autoComplete="email"
              sx={{
                backgroundColor: "#f1f5f9",
                borderRadius: 2,
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              sx={{
                backgroundColor: "#f1f5f9",
                borderRadius: 2,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <MUILink
              component={Link}
              to="/forgot-password"
              underline="hover"
              sx={{ display: "block", textAlign: "right", mt: 1, mb: 3 }}
            >
              Forgot your password?
            </MUILink>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mb: 2,
                py: 1.25,
                borderRadius: "12px",
                fontWeight: 600,
                backgroundColor: "#1e3a8a",
                "&:hover": { backgroundColor: "#1e40af" },
              }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Login"
              )}
            </Button>

            <Typography align="center" sx={{ fontSize: 14 }}>
              Don’t have an account?{" "}
              <MUILink component={Link} to="/signup" underline="hover">
                Sign up
              </MUILink>
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 3, fontSize: 11 }}
            >
              By logging in, you agree to our{" "}
              <MUILink component={Link} to="/terms" underline="hover">
                Terms
              </MUILink>{" "}
              and{" "}
              <MUILink component={Link} to="/privacy" underline="hover">
                Privacy Policy
              </MUILink>
              .
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
}
